/**
 * ForgeAI Govern™ - Admin → Tenant Settings page
 *
 * Tenant-wide toggles that previously only existed as API endpoints:
 *   - HIPAA BAA acknowledgement (gate for PHI-accessing assets)
 *   - Data residency required region + enforcement mode (log | deny)
 *   - EU AI Act in scope flag
 *   - Read-only tenant identity + plan
 *
 * The alert webhook lives under Admin → Integrations since it's an
 * outbound destination. This page is the rest.
 */

function fmtDate(s) { return s ? new Date(s).toLocaleString() : '—'; }

export const tenantSettings = {
  async page() {
    let t = {};
    try { t = (await API.getTenantSettings()).data || {}; } catch { /* */ }

    const baaSigned = !!t.hipaa_baa_signed;
    const euInScope = !!t.eu_ai_act_in_scope;
    const residency = t.data_residency || 'us';
    const residencyMode = t.data_residency_mode || 'log';

    return `
      <div class="page-header">
        <div>
          <h2>Tenant Settings</h2>
          <p>Organization-wide policy switches that govern how this tenant operates.</p>
        </div>
      </div>

      <!-- ===== Identity card ===== -->
      <section class="card" style="margin-bottom:var(--sp-6)">
        <div class="card-header">
          <h3>Organization</h3>
        </div>
        <div class="card-body">
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">Name</span>
              <span>${t.name || '—'}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Plan</span>
              <span><span class="badge badge-${t.plan === 'enterprise' ? 'approved' : t.plan === 'professional' ? 'info' : 'draft'}">${App.formatLabel(t.plan) || '—'}</span></span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Status</span>
              <span><span class="badge badge-${t.status === 'active' ? 'approved' : 'draft'}">${App.formatLabel(t.status) || '—'}</span></span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Created</span>
              <span>${fmtDate(t.created_at)}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Tenant ID</span>
              <span><code class="inline-code">${t.id || '—'}</code></span>
            </div>
          </div>
        </div>
      </section>

      <!-- ===== MFA enrollment ===== -->
      <section class="card" style="margin-bottom:var(--sp-6)" id="mfa-enroll-card">
        <div class="card-header">
          <h3>Authenticator (MFA)</h3>
        </div>
        <div class="card-body">
          <p style="margin-bottom:12px;color:var(--text-secondary)">
            Admin and governance-lead accounts must enroll TOTP before approving, deciding, exporting CAIO packages, or creating backups. Store the secret in an authenticator app; this platform does not load QR images from the public internet.
          </p>
          <div id="mfa-enroll-status"></div>
          <div class="form-row" style="gap:8px;align-items:flex-end">
            <button type="button" class="btn btn-primary" id="mfa-start-enroll">Start enrollment</button>
          </div>
          <div id="mfa-enroll-result" class="hidden" style="margin-top:12px">
            <label for="mfa-secret-display">Enrollment secret</label>
            <input type="text" id="mfa-secret-display" class="form-input" readonly autocomplete="off">
            <p id="mfa-otpauth-display" class="text-secondary" style="margin:8px 0;word-break:break-all;font-size:12px"></p>
            <label for="mfa-confirm-code">Confirmation code</label>
            <input type="text" id="mfa-confirm-code" class="form-input" maxlength="6" inputmode="numeric" autocomplete="one-time-code" placeholder="6-digit code">
            <button type="button" class="btn btn-primary" id="mfa-confirm-enroll" style="margin-top:8px">Confirm MFA</button>
          </div>
        </div>
      </section>

      <section class="integration-card" style="margin-bottom:var(--sp-6)">
        <header class="integration-hero">
          <div class="integration-identity">
            <div class="integration-icon integration-icon-primary" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 12 11 14l5-5"/><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            </div>
            <div class="integration-title">
              <h3>HIPAA Business Associate Agreement</h3>
              <p class="integration-subtitle">Required before any AI system can process Protected Health Information (PHI) on this tenant. Signed records are preserved in the tamper-evident audit log.</p>
              <div class="integration-meta">
                <span class="meta-pill">45 CFR § 164</span>
                <span class="meta-pill">PHI access gating</span>
                <span class="meta-pill">6-year retention</span>
              </div>
            </div>
          </div>
          <div class="integration-controls">
            <span class="status-pill ${baaSigned ? 'status-pill-ok' : 'status-pill-off'}">
              <span class="status-pill-dot"></span>${baaSigned ? 'Acknowledged' : 'Not acknowledged'}
            </span>
          </div>
        </header>
        <div class="integration-body">
          ${baaSigned ? `
            <div class="alert alert-success" style="margin-bottom:0">
              <strong>BAA on file.</strong> Acknowledged ${fmtDate(t.hipaa_baa_signed_at)}${t.hipaa_baa_signer_name ? ' by ' + t.hipaa_baa_signer_name : ''}. This record is preserved in the audit log; contact Cana Ridge Technologies support to amend or revoke.
            </div>` : `
            <div class="field-section" style="padding:0 0 var(--sp-4) 0;border-bottom:none;grid-template-columns:1fr">
              <div class="attestation-box">
                <p style="margin-bottom:var(--sp-3);color:var(--text);font-weight:500">
                  By acknowledging, you confirm on behalf of <strong>${t.name || 'this organization'}</strong> that:
                </p>
                <ul>
                  <li>A Business Associate Agreement meeting the requirements of HIPAA 45 CFR § 164 is executed between your organization and Cana Ridge Technologies LLC.</li>
                  <li>Cana Ridge AI Command is authorized to process Protected Health Information on this tenant's behalf.</li>
                  <li>You are a duly authorized representative of the organization with authority to bind it to this agreement.</li>
                </ul>
                <p style="margin-top:var(--sp-3);color:var(--text-muted);font-size:12px">
                  Acknowledgement enables PHI-accessing AI system configuration, applies HIPAA-required audit retention, and is stored as a permanent entry in the tamper-evident audit log.
                </p>
              </div>
              <div style="margin-top:var(--sp-4);display:flex;gap:var(--sp-3);align-items:center">
                <button type="button" class="btn btn-primary" id="baa-ack-btn">I acknowledge the BAA</button>
                <span class="text-muted" style="font-size:12px">Admin-only action.</span>
              </div>
            </div>`}
        </div>
      </section>

      <!-- ===== Data residency ===== -->
      <section class="integration-card" style="margin-bottom:var(--sp-6)">
        <header class="integration-hero">
          <div class="integration-identity">
            <div class="integration-icon integration-icon-primary" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15 15 0 0 1 4 10 15 15 0 0 1-4 10 15 15 0 0 1-4-10 15 15 0 0 1 4-10z"/></svg>
            </div>
            <div class="integration-title">
              <h3>Data Residency</h3>
              <p class="integration-subtitle">Restrict where requests can be processed. Choose a required region and whether to log violations or block them outright.</p>
            </div>
          </div>
          <div class="integration-controls">
            <span class="status-pill ${residencyMode === 'deny' ? 'status-pill-ok' : 'status-pill-off'}">
              <span class="status-pill-dot"></span>${residencyMode === 'deny' ? 'Enforced' : 'Log only'}
            </span>
          </div>
        </header>
        <form id="residency-form" class="integration-body">
          <div class="field-section">
            <div class="field-section-head">
              <h4>Required region</h4>
              <p>Processing must happen inside this region. Choose Any to disable the check.</p>
            </div>
            <div class="form-group">
              <label for="residency-region">Region</label>
              <select id="residency-region">
                <option value="us" ${residency === 'us' ? 'selected' : ''}>United States</option>
                <option value="eu" ${residency === 'eu' ? 'selected' : ''}>European Union</option>
                <option value="any" ${residency === 'any' ? 'selected' : ''}>Any (no restriction)</option>
              </select>
            </div>
          </div>
          <div class="field-section">
            <div class="field-section-head">
              <h4>Enforcement mode</h4>
              <p>Log-only records violations to the audit trail. Deny blocks offending requests with a 451 response.</p>
            </div>
            <div class="form-group">
              <label for="residency-mode">Mode</label>
              <select id="residency-mode">
                <option value="log" ${residencyMode === 'log' ? 'selected' : ''}>Log only (detect + audit)</option>
                <option value="deny" ${residencyMode === 'deny' ? 'selected' : ''}>Deny (block with 451)</option>
              </select>
              <small>Flip to <strong>Deny</strong> only after confirming traffic is actually served from your required region — otherwise you may lock out your own admins.</small>
            </div>
          </div>
          <footer class="integration-footer">
            <div class="integration-health">
              <span class="health-dot health-dot-ok"></span>
              <div>
                <div class="health-line"><strong>Current:</strong> Region ${App.formatLabel(residency === 'us' ? 'United States' : residency === 'eu' ? 'European Union' : residency)} · Mode ${App.formatLabel(residencyMode === 'log' ? 'Log only' : 'Deny')}</div>
              </div>
            </div>
            <div class="integration-actions">
              <button type="submit" class="btn btn-primary">Save residency policy</button>
            </div>
          </footer>
        </form>
      </section>

      <!-- ===== EU AI Act ===== -->
      <section class="integration-card">
        <header class="integration-hero">
          <div class="integration-identity">
            <div class="integration-icon integration-icon-warning" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            </div>
            <div class="integration-title">
              <h3>EU AI Act Scope</h3>
              <p class="integration-subtitle">Surface EU AI Act controls and obligations for tenants with EU operations or EU-resident data subjects.</p>
              <div class="integration-meta">
                <span class="meta-pill">Annex III classification</span>
                <span class="meta-pill">Art. 9-15 obligations</span>
                <span class="meta-pill">Post-market monitoring</span>
              </div>
            </div>
          </div>
          <div class="integration-controls">
            <span class="status-pill ${euInScope ? 'status-pill-ok' : 'status-pill-off'}">
              <span class="status-pill-dot"></span>${euInScope ? 'In scope' : 'Out of scope'}
            </span>
            <label class="toggle-switch" title="EU AI Act in scope">
              <input type="checkbox" id="eu-scope-toggle" ${euInScope ? 'checked' : ''}>
              <span class="toggle-track"><span class="toggle-thumb"></span></span>
            </label>
          </div>
        </header>
        <div class="integration-body">
          <div class="field-section" style="padding:0;border-bottom:none;grid-template-columns:1fr">
            <p style="font-size:13px;line-height:1.6;color:var(--text-secondary);margin:0">
              When enabled, the platform activates <code class="inline-code">EU-AI-1</code> through <code class="inline-code">EU-AI-4</code> controls (risk classification, high-risk obligations, transparency, post-market monitoring) in the compliance matrix, and flags AI use cases for EU jurisdictional review during intake. Changing this setting takes effect immediately; no restart required.
            </p>
          </div>
        </div>
      </section>
    `;
  },

  async init() {
    const startBtn = document.getElementById('mfa-start-enroll');
    if (startBtn) {
      startBtn.addEventListener('click', async () => {
        try {
          const data = await API.enrollMfa();
          const box = document.getElementById('mfa-enroll-result');
          const secret = document.getElementById('mfa-secret-display');
          const otp = document.getElementById('mfa-otpauth-display');
          if (box) box.classList.remove('hidden');
          if (secret) secret.value = data.secret || '';
          if (otp) otp.textContent = data.otpauth_url || '';
          App.toast('Enrollment secret issued. Confirm with a 6-digit code.', 'success');
        } catch (err) {
          App.toast(`MFA enroll failed: ${err?.message || 'error'}`, 'error');
        }
      });
    }
    const confirmBtn = document.getElementById('mfa-confirm-enroll');
    if (confirmBtn) {
      confirmBtn.addEventListener('click', async () => {
        const code = document.getElementById('mfa-confirm-code')?.value;
        try {
          await API.verifyMfa(code);
          if (API.user) {
            API.user.mfa_enabled = true;
            API.user.mfa_enrollment_required = false;
            sessionStorage.setItem('forgeai_user', JSON.stringify(API.user));
          }
          if (typeof App.syncMfaEnrollmentBanner === 'function') App.syncMfaEnrollmentBanner();
          App.toast('MFA enabled', 'success');
          App.navigate('tenant-settings');
        } catch (err) {
          App.toast(`MFA confirm failed: ${err?.message || 'error'}`, 'error');
        }
      });
    }

    // BAA acknowledgement
    const baaBtn = document.getElementById('baa-ack-btn');
    if (baaBtn) {
      baaBtn.addEventListener('click', async () => {
        if (!confirm('Acknowledge the HIPAA Business Associate Agreement? This is a permanent, audit-logged action.')) return;
        try {
          await API.acknowledgeBaa();
          App.toast('BAA acknowledged', 'success');
          App.navigate('tenant-settings');
        } catch (err) {
          App.toast(`Failed: ${err?.message || 'error'}`, 'error');
        }
      });
    }

    // Residency form
    const residencyForm = document.getElementById('residency-form');
    if (residencyForm) {
      residencyForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const region = document.getElementById('residency-region').value;
        const mode = document.getElementById('residency-mode').value;
        if (mode === 'deny' && region !== 'any') {
          if (!confirm(`Enable deny mode for region "${region}"? Requests processed outside this region will be rejected with HTTP 451. Make sure your admin traffic originates from the required region.`)) return;
        }
        try {
          await API.updateTenantSettings({ data_residency: region, data_residency_mode: mode });
          App.toast('Residency policy updated', 'success');
          App.navigate('tenant-settings');
        } catch (err) {
          App.toast(`Failed: ${err?.message || 'error'}`, 'error');
        }
      });
    }

    // EU AI Act toggle — save immediately on change
    const euToggle = document.getElementById('eu-scope-toggle');
    if (euToggle) {
      euToggle.addEventListener('change', async () => {
        try {
          await API.updateTenantSettings({ eu_ai_act_in_scope: euToggle.checked });
          App.toast(`EU AI Act ${euToggle.checked ? 'enabled' : 'disabled'}`, 'success');
        } catch (err) {
          App.toast(`Failed: ${err?.message || 'error'}`, 'error');
          euToggle.checked = !euToggle.checked; // revert
        }
      });
    }
  },
};
